import axios from 'axios'
import { baseUrl, requestConfig } from './base.js'
import Cookies from 'js-cookie'
import { Message } from 'element-ui'
import router from '@/router'

// 是否正在退出登录（防止多个 401 并发触发多次跳转）
let isLoggingOut = false

// 请求去重工具函数
const pendingRequests = new Map()

// 防抖和节流相关
const debounceMap = new Map()
const throttleMap = new Map()
// 请求结果缓存，用于判断是否应该跳过防抖和节流
const requestResultCache = new Map()
// 请求队列管理
const requestQueue = {
  queue: [],
  processing: false,
  maxConcurrent: requestConfig.queueConfig.maxConcurrent, // 最大并发请求数
  currentConcurrent: 0
}

// 工具函数
const utils = {
  // 生成请求唯一标识
  generateRequestKey(config) {
    return `${config.method}-${config.url}-${JSON.stringify(config.params || {})}-${JSON.stringify(config.data || {})}`
  },

  // 防抖函数
  debounce(func, delay, key) {
    if (debounceMap.has(key)) {
      clearTimeout(debounceMap.get(key))
    }
    const timer = setTimeout(() => {
      func()
      debounceMap.delete(key)
    }, delay)
    debounceMap.set(key, timer)
  },

  // 节流函数
  throttle(func, delay, key) {
    if (throttleMap.has(key)) {
      return Promise.reject(new Error('请求过于频繁，请稍后再试'))
    }
    throttleMap.set(key, true)
    setTimeout(() => {
      throttleMap.delete(key)
    }, delay)
    return func()
  },

  // 智能防抖函数 - 失败时不拦截
  smartDebounce(func, delay, key) {
    // 检查上次请求是否失败
    const lastResult = requestResultCache.get(key)
    if (lastResult === 'failed') {
      // 如果上次请求失败，直接执行，不进行防抖
      requestResultCache.delete(key)
      return func()
    }

    // 如果上次请求成功或没有记录，进行防抖
    if (debounceMap.has(key)) {
      clearTimeout(debounceMap.get(key))
    }
    const timer = setTimeout(() => {
      func()
      debounceMap.delete(key)
    }, delay)
    debounceMap.set(key, timer)
  },

  // 智能节流函数 - 失败时不拦截
  smartThrottle(func, delay, key) {
    // 检查上次请求是否失败
    const lastResult = requestResultCache.get(key)
    if (lastResult === 'failed') {
      // 如果上次请求失败，直接执行，不进行节流
      requestResultCache.delete(key)
      return func()
    }

    // 如果上次请求成功或没有记录，进行节流
    if (throttleMap.has(key)) {
      return Promise.reject(new Error('请求过于频繁，请稍后再试'))
    }
    throttleMap.set(key, true)
    setTimeout(() => {
      throttleMap.delete(key)
    }, delay)
    return func()
  },

  // 记录请求结果
  recordRequestResult(key, isSuccess) {
    requestResultCache.set(key, isSuccess ? 'success' : 'failed')
    // 设置过期时间，避免缓存过多
    setTimeout(() => {
      requestResultCache.delete(key)
    }, 3000) // 30秒后清除缓存
  },
  // 延迟函数
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  },
}
// 创建 axios 实例
const service = axios.create({
  baseURL: baseUrl,
  timeout: requestConfig.timeout,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})
// 请求拦截器
service.interceptors.request.use(
  config => {
    // 添加 token（如果有的话）
    const token = Cookies.get('token')
    if (token) {
      config.headers.Authorization = token
    }
    // 请求去重
    const requestKey = utils.generateRequestKey(config)
    pendingRequests.set(requestKey, true)
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 退出登录：清除 token 并跳转登录页
function handleLogout(msg) {
  if (isLoggingOut) return
  isLoggingOut = true

  // 清除所有身份相关信息
  Cookies.remove('token')
  Cookies.remove('token_agent')
  Cookies.remove('token_bidder')
  localStorage.clear()

  Message.error(msg || '登录已过期，请重新登录')

  setTimeout(() => {
    isLoggingOut = false
    // 判断当前路径避免重复跳转
    if (router.currentRoute.path !== '/login') {
      router.replace('/login')
    }
  }, 1500)
}

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 清除请求去重标记
    const requestKey = utils.generateRequestKey(response.config)
    pendingRequests.delete(requestKey)

    const { data } = response

    // 业务层 401：token 过期或无效
    if (data.code === 401) {
      handleLogout(data.msg || '登录已过期，请重新登录')
      return Promise.reject(new Error(data.msg || '登录已过期'))
    }

    // 业务层其他错误码（非 200）提示但不拦截，由业务调用方自行处理
    return data
  },
  async error => {
    // 清除请求去重标记
    if (error.config) {
      const requestKey = utils.generateRequestKey(error.config)
      pendingRequests.delete(requestKey)
    }

    let msg = '网络错误'
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          msg = (data && data.msg) || '请求参数错误'
          break
        case 401:
          handleLogout((data && data.msg) || '未授权，请重新登录')
          return Promise.reject(new Error('未授权，请重新登录'))
        case 403:
          msg = '拒绝访问'
          break
        case 404:
          msg = '请求地址不存在'
          break
        case 500:
          msg = (data && data.msg) || '服务器内部错误'
          break
        case 502:
          msg = '网关错误'
          break
        case 503:
          msg = '服务不可用'
          break
        case 504:
          msg = '网关超时'
          break
        default:
          msg = (data && data.msg) || `连接错误${status}`
      }
    } else if (error.code === 'ECONNABORTED') {
      msg = '请求超时，请检查网络后重试'
    } else if (error.message === 'Network Error') {
      msg = '网络连接异常，请检查网络设置'
    }

    Message.error(msg)
    return Promise.reject(new Error(msg))
  }
)

// 请求方法封装
class Request {
  constructor() {
    this.defaultConfig = {
      debounceDelay: requestConfig.debounceConfig.defaultDelay, // 默认防抖延迟
      throttleDelay: requestConfig.throttleConfig.defaultDelay, // 默认节流延迟
      enableDebounce: false, // 默认不启用防抖
      enableThrottle: false // 默认不启用节流
    }
  }

  // 处理请求配置
  _processConfig(url, config = {}) {
    const finalConfig = {
      ...this.defaultConfig,
      ...config,
      url
    }

    // 生成请求唯一标识
    const requestKey = utils.generateRequestKey({
      method: finalConfig.method || 'get',
      url: finalConfig.url,
      params: finalConfig.params || {},
      data: finalConfig.data || {}
    })

    return { finalConfig, requestKey }
  }

  // 执行请求
  async _executeRequest(config, requestKey) {
    return new Promise((resolve, reject) => {
      const execute = () => {
        service(config)
          .then(response => {
            // 记录请求成功
            if (requestKey) {
              utils.recordRequestResult(requestKey, true)
            }
            resolve(response)
          })
          .catch(error => {
            // 记录请求失败
            if (requestKey) {
              utils.recordRequestResult(requestKey, false)
            }
            reject(error)
          })
          .finally(() => {
            // 请求完成后减少并发计数
            requestQueue.currentConcurrent--
            // 处理队列中的下一个请求
            this._processQueue()
          })
      }
      // 队列管理
      if (requestQueue.currentConcurrent >= requestQueue.maxConcurrent) {
        requestQueue.queue.push(execute)
        return
      }
      requestQueue.currentConcurrent++
      execute()
    })
  }

  // 处理请求队列
  _processQueue() {
    if (requestQueue.queue.length > 0 && requestQueue.currentConcurrent < requestQueue.maxConcurrent) {
      const nextRequest = requestQueue.queue.shift()
      if (nextRequest) {
        requestQueue.currentConcurrent++
        nextRequest()
      }
    }
  }

  // GET 请求
  get(url, params = {}, config = {}) {
    const { finalConfig, requestKey } = this._processConfig(url, {
      method: 'get',
      params,
      ...config
    })

    // 智能防抖处理 - 失败时不拦截
    if (finalConfig.enableDebounce) {
      return new Promise((resolve, reject) => {
        utils.smartDebounce(() => {
          this._executeRequest(finalConfig, requestKey).then(resolve).catch(reject)
        }, finalConfig.debounceDelay, requestKey)
      })
    }

    // 智能节流处理 - 失败时不拦截
    if (finalConfig.enableThrottle) {
      return utils.smartThrottle(() => {
        return this._executeRequest(finalConfig, requestKey)
      }, finalConfig.throttleDelay, requestKey)
    }

    return this._executeRequest(finalConfig, requestKey)
  }

  // POST 请求
  post(url, data = {}, config = {}) {
    const { finalConfig, requestKey } = this._processConfig(url, {
      method: 'post',
      data,
      ...config
    })

    // 智能防抖处理 - 失败时不拦截
    if (finalConfig.enableDebounce) {
      return new Promise((resolve, reject) => {
        utils.smartDebounce(() => {
          this._executeRequest(finalConfig, requestKey).then(resolve).catch(reject)
        }, finalConfig.debounceDelay, requestKey)
      })
    }

    // 智能节流处理 - 失败时不拦截
    if (finalConfig.enableThrottle) {
      return utils.smartThrottle(() => {
        return this._executeRequest(finalConfig, requestKey)
      }, finalConfig.throttleDelay, requestKey)
    }

    return this._executeRequest(finalConfig, requestKey)
  }

  // PUT 请求
  put(url, data = {}, config = {}) {
    const { finalConfig, requestKey } = this._processConfig(url, {
      method: 'put',
      data,
      ...config
    })

    // 智能防抖处理 - 失败时不拦截
    if (finalConfig.enableDebounce) {
      return new Promise((resolve, reject) => {
        utils.smartDebounce(() => {
          this._executeRequest(finalConfig, requestKey).then(resolve).catch(reject)
        }, finalConfig.debounceDelay, requestKey)
      })
    }

    // 智能节流处理 - 失败时不拦截
    if (finalConfig.enableThrottle) {
      return utils.smartThrottle(() => {
        return this._executeRequest(finalConfig, requestKey)
      }, finalConfig.throttleDelay, requestKey)
    }

    return this._executeRequest(finalConfig, requestKey)
  }

  // DELETE 请求
  delete(url, params = {}, config = {}) {
    const { finalConfig, requestKey } = this._processConfig(url, {
      method: 'delete',
      params,
      ...config
    })

    // 智能防抖处理 - 失败时不拦截
    if (finalConfig.enableDebounce) {
      return new Promise((resolve, reject) => {
        utils.smartDebounce(() => {
          this._executeRequest(finalConfig, requestKey).then(resolve).catch(reject)
        }, finalConfig.debounceDelay, requestKey)
      })
    }

    // 智能节流处理 - 失败时不拦截
    if (finalConfig.enableThrottle) {
      return utils.smartThrottle(() => {
        return this._executeRequest(finalConfig, requestKey)
      }, finalConfig.throttleDelay, requestKey)
    }

    return this._executeRequest(finalConfig, requestKey)
  }

  // PATCH 请求
  patch(url, data = {}, config = {}) {
    const { finalConfig, requestKey } = this._processConfig(url, {
      method: 'patch',
      data,
      ...config
    })

    // 智能防抖处理 - 失败时不拦截
    if (finalConfig.enableDebounce) {
      return new Promise((resolve, reject) => {
        utils.smartDebounce(() => {
          this._executeRequest(finalConfig, requestKey).then(resolve).catch(reject)
        }, finalConfig.debounceDelay, requestKey)
      })
    }

    // 智能节流处理 - 失败时不拦截
    if (finalConfig.enableThrottle) {
      return utils.smartThrottle(() => {
        return this._executeRequest(finalConfig, requestKey)
      }, finalConfig.throttleDelay, requestKey)
    }

    return this._executeRequest(finalConfig, requestKey)
  }

  // 文件上传
  upload(url, file, config = {}) {
    const formData = new FormData()
    formData.append('file', file)

    const { finalConfig, requestKey } = this._processConfig(url, {
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    })

    return this._executeRequest(finalConfig, requestKey)
  }

  // 文件下载
  download(url, params = {}, filename = '') {
    const { finalConfig, requestKey } = this._processConfig(url, {
      method: 'get',
      params,
      responseType: 'blob'
    })

    return this._executeRequest(finalConfig, requestKey).then(response => {
      const blob = new Blob([response.data])
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    })
  }

  // 并发请求
  all(requests) {
    return Promise.all(requests)
  }

  // 竞速请求（取最快返回的结果）
  race(requests) {
    return Promise.race(requests)
  }

  // 取消请求
  cancelRequest(url) {
    // 这里可以实现请求取消逻辑
    console.log(`取消请求: ${url}`)
  }

  // 设置默认配置
  setDefaultConfig(config) {
    this.defaultConfig = { ...this.defaultConfig, ...config }
  }

  // 获取当前配置
  getDefaultConfig() {
    return { ...this.defaultConfig }
  }

  // 清除防抖缓存
  clearDebounceCache() {
    debounceMap.clear()
  }

  // 清除节流缓存
  clearThrottleCache() {
    throttleMap.clear()
  }

  // 清除所有缓存
  clearAllCache() {
    this.clearDebounceCache()
    this.clearThrottleCache()
    this.clearRequestResultCache()
    pendingRequests.clear()
  }

  // 清除请求结果缓存
  clearRequestResultCache() {
    requestResultCache.clear()
  }

  // 清除特定请求的结果缓存
  clearRequestResult(key) {
    requestResultCache.delete(key)
  }

  // 获取队列状态
  getQueueStatus() {
    return {
      queueLength: requestQueue.queue.length,
      currentConcurrent: requestQueue.currentConcurrent,
      maxConcurrent: requestQueue.maxConcurrent
    }
  }

  // 设置最大并发数
  setMaxConcurrent(max) {
    requestQueue.maxConcurrent = max
  }
}

// 创建请求实例
const request = new Request()

// 导出请求实例和原始 axios 实例
export default request
export { service as axios }
